import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Users, LifeBuoy, ClipboardList, Bell, Loader2 } from "lucide-react";

const TeamOverview = () => {
    const { user } = useAuth();
    const [data, setData] = useState({ tasks: [], notifications: [] });
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const [tasks, notif] = await Promise.all([
                    api.get("/team/tasks", { params: { scope: "mine" } }),
                    api.get("/notifications"),
                ]);
                setData({ tasks: tasks.data.tasks, notifications: notif.data.notifications });
            } catch {}
            setLoading(false);
        })();
    }, []);

    return (
        <div className="p-6 md:p-8" data-testid="team-overview">
            <div className="text-xs uppercase tracking-widest text-brand-gold font-semibold">Welcome back, {user?.name?.split(" ")[0]}</div>
            <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Team overview</h1>
            <p className="text-muted-foreground mt-1">Your assigned work and recent activity in one place.</p>

            {loading ? <Loader2 className="w-6 h-6 animate-spin text-primary mt-6" /> : (
                <div className="mt-6 grid md:grid-cols-3 gap-4">
                    <div className="md:col-span-2 rounded-2xl bg-card border p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="font-display text-lg font-semibold">My tasks</h3>
                            <Link to="/team/tasks" className="text-xs text-primary">Open board →</Link>
                        </div>
                        {data.tasks.length === 0 ? (
                            <div className="p-6 text-center text-sm text-muted-foreground">
                                <ClipboardList className="w-8 h-8 mx-auto opacity-40 mb-1" /> Nothing on your plate yet.
                            </div>
                        ) : (
                            <div className="space-y-2">
                                {data.tasks.slice(0, 6).map((t) => (
                                    <div key={t.id} className="rounded-xl bg-muted/40 p-3 flex items-center justify-between">
                                        <div>
                                            <div className="text-sm font-semibold">{t.title}</div>
                                            <div className="text-xs text-muted-foreground uppercase tracking-widest">{t.status.replace(/_/g, " ")} · {t.priority}</div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                    <div className="rounded-2xl bg-card border p-6">
                        <h3 className="font-display text-lg font-semibold mb-3 flex items-center gap-2"><Bell className="w-4 h-4" /> Notifications</h3>
                        {data.notifications.length === 0 ? (
                            <p className="text-sm text-muted-foreground">You're all caught up 🎉</p>
                        ) : (
                            <div className="space-y-2">
                                {data.notifications.slice(0, 8).map((n) => (
                                    <div key={n.id} className={`rounded-xl p-3 text-sm ${n.read ? "bg-muted/30" : "bg-primary/5 border border-primary/20"}`}>
                                        <div className="font-semibold">{n.title}</div>
                                        {n.body && <div className="text-xs text-muted-foreground">{n.body}</div>}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="md:col-span-3 grid md:grid-cols-3 gap-3">
                        <QuickLink to="/team/sales" icon={Users} label="Sales pipeline" desc="Track leads, follow-ups, conversions" />
                        <QuickLink to="/team/support" icon={LifeBuoy} label="Customer support" desc="Answer tickets & queries" />
                        <QuickLink to="/team/tasks" icon={ClipboardList} label="Task board" desc="Assign and move work" />
                    </div>
                </div>
            )}
        </div>
    );
};

const QuickLink = ({ to, icon: Icon, label, desc }) => (
    <Link to={to} className="rounded-2xl bg-gradient-to-br from-primary to-primary/80 text-primary-foreground p-5 hover:scale-[1.02] transition-transform">
        <Icon className="w-6 h-6 mb-3" />
        <div className="font-display text-lg font-bold">{label}</div>
        <div className="text-sm opacity-90">{desc}</div>
    </Link>
);

export default TeamOverview;
